import React, { useContext, useMemo, useState } from 'react';
import { Box, ResponsiveContext, Text, ThemeContext } from 'grommet';
import { normalizeColor } from 'grommet/utils';
import { IconChevronDown } from '@tabler/icons-react';
import styled from 'styled-components';
import Icon from '../../Icon';
import DateInput from '../../DateInput';
import AgingBalancesPieChart from '../../AgingBalancesPieChart';
import { AGING_BALANCE_GRAPH_COLOR_MAPPING } from '../../../constants/Colors';
import {
    agingLine, collectionsHeadline, collectionsLine, collectionsState, collectionsWeeks, daysLine, lastWeeks, latestPaymentPeriod, overdueAmount, paymentPeriodPoints,
    percent, salesLine, usesMedian,
} from '../../../utils/chart-data';
import { ChartSwatch } from '../../charts/ChartFigure';
import { formatCurrencySI } from '../../../utils/numbers';
import PairedBars from '../../charts/PairedBars';
import PaymentLine from '../../charts/PaymentLine';
import Sparkline, { SegmentSpark } from '../../charts/Sparkline';
import { ChartWeeksSkeleton, SkeletonText } from '../../charts/ChartSkeleton';
import { CollectionsChart, REPORT, VIEW_REPORT, useCollections } from '../../charts/CollectionsCard';
import { formatDate } from '../../../utils/dates';

// Four numbers, each opening its chart. One open at a time, and the dashboard always starts on
// Collections. Only the open chart is drawn.
//
// Every tile reads the same way: label, number, one plain line, and a small picture. The tiles go
// four across, then two by two, then a list on a phone, one step at a time as the page narrows.

const TWO = '560px';
const ALL = '1000px';

const Wrap = styled.div`container-type: inline-size;`;
// The 1px gap shows the frame's line color through, so tiles get dividers whichever way they wrap
const Tiles = styled.div`
    display: grid;
    gap: 1px;
    background: ${(props) => props.$line};
    grid-template-columns: minmax(0, 1fr);
    & > * { order: var(--order); }
    /* A list: the open chart sits under its own tile, and the pictures are left out. A phone
       doesn't draw them at all; this covers a narrow card on a wider screen. */
    [data-spark] { display: none; }
    @container (min-width: ${TWO}) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        /* Side by side, the chart opens under all the tiles */
        & > [data-body] { order: 100; }
        [data-spark] { display: block; }
    }
    @container (min-width: ${ALL}) { grid-template-columns: repeat(var(--tiles), minmax(0, 1fr)); }
`;
const Body = styled.div`grid-column: 1 / -1; background: ${(props) => props.$bg};`;
const TileButton = styled.button`
    all: unset;
    /* After the reset, which would otherwise clear it */
    order: var(--order);
    /* A button centers what's in it, so a tile with no number yet would sit lower than its
       neighbours. As a column from the top, every tile's label starts on the same line. */
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    box-sizing: border-box;
    cursor: pointer;
    padding: 18px 20px 14px;
    /* The open tile keeps the card's background, underlined like a tab: a filled tile is a big
       block of color. Hover is the faint tint menus and list rows use. */
    background: ${(props) => props.$bg};
    box-shadow: ${(props) => (props.$open ? `inset 0 -2px 0 ${props.$text}` : 'none')};
    &:hover { background: ${(props) => (props.$open ? props.$bg : props.$hoverBg)}; }
    &:focus-visible { outline: 2px solid ${(props) => props.$accent}; outline-offset: -2px; }
`;
const Chevron = styled.span`display: inline-flex; transition: transform .15s; transform: rotate(${(props) => (props.$open ? '180deg' : '0')});`;

const AGE_BUCKETS = ['0', '1', '31', '61', '91'];
const sum = (rows, key) => rows.reduce((total, row) => total + (row[key] || 0), 0);

// Label, number, one line, picture. The line never wraps: what doesn't fit ends in "…", whole on hover.
const TileFace = ({ label, value, note, tone, empty, isLoading, open, spark, showSpark }) => (
    <Box>
        <Box direction="row" justify="between" align="center" gap="small">
            <Text color="text-weak" truncate title={label}>{label}</Text>
            <Chevron $open={open}><Icon SelectedIcon={IconChevronDown} color="text-weak" size="small" /></Chevron>
        </Box>
        {isLoading && (
            <>
                <SkeletonText width="112px" height="28px" lineHeight="36px" />
                <SkeletonText width="136px" lineHeight="20px" />
            </>
        )}
        {!isLoading && value == null && <Text color="text-weak" truncate title={empty}>{empty}</Text>}
        {!isLoading && value != null && (
            <>
                <Text size="30px" weight={600} style={{ lineHeight: 1.2 }}>{value}</Text>
                <Text size="small" color={tone || 'text-weak'} weight={tone ? 500 : undefined} truncate title={note} style={{ minHeight: 20 }}>{note}</Text>
            </>
        )}
        {/* No picture without a number: a flat line at $0 says nothing */}
        {showSpark && !isLoading && value != null && <Box data-spark margin={{ top: 'small' }}>{spark}</Box>}
    </Box>
);

// The open chart's title, with one sentence under it that says what the chart shows
const Headline = ({ title, line, action }) => (
    <Box direction="row" justify="between" align="start" gap="small" wrap style={{ rowGap: '12px' }}>
        <Box gap="xxsmall">
            <Text size="large" weight={500}>{title}</Text>
            {line && <Text color="text-weak">{line}</Text>}
        </Box>
        {action}
    </Box>
);

// What was sold beside what came in, so the gap between them reads at a glance
const SalesBody = ({ rows, currency, sold, paid }) => (
    <Box gap="small">
        <Box direction="row" gap="medium" wrap>
            <Box direction="row" gap="xsmall" align="center"><ChartSwatch color="chart-muted" /><Text size="small" color="text-weak">{`Sold ${formatCurrencySI(sold, currency)}`}</Text></Box>
            <Box direction="row" gap="xsmall" align="center"><ChartSwatch color="barchart-primary" /><Text size="small" color="text-weak">{`Collected ${formatCurrencySI(paid, currency)}`}</Text></Box>
        </Box>
        <PairedBars rows={rows} currency={currency} height={260} scale />
    </Box>
);

const DashboardStrip = ({ dashboardData, monthlySales, averagePaymentPeriod, currency, reportDate, setReportDate }) => {
    const theme = useContext(ThemeContext);
    const color = (name) => normalizeColor(name, theme);
    // Undefined until a tile is clicked: the first tile opens, which is Collections unless it failed
    const [chosen, setChosen] = useState();
    const isPhone = useContext(ResponsiveContext) === 'small';

    const { data: collections, error: collectionsError } = useCollections();
    // A failed report drops its tile, as the Collections card used to hide, rather than show $0.
    // Keyed on the error, since before the first request starts nothing is loading yet either.
    const collectionsFailed = collectionsError !== undefined && !collections;
    const shownWeeks = useMemo(() => (collections && isPhone ? lastWeeks(collections, 13) : collections), [collections, isPhone]);
    // The last quarter of weeks. The lit bar is the tile's number: this week's estimate when there
    // is one, otherwise last week.
    const recentWeeks = useMemo(() => {
        if (!collections) return [];
        const weeks = collectionsWeeks(collections).filter((week) => !week.stub).map((week) => week.payments || 0);
        return collectionsState(collections) === 'typical' ? [...weeks.slice(-12), collections.estimate.expected] : weeks.slice(-13);
    }, [collections]);
    const points = useMemo(() => paymentPeriodPoints(averagePaymentPeriod?.data), [averagePaymentPeriod]);
    const latest = useMemo(() => latestPaymentPeriod(points), [points]);
    const balances = dashboardData?.aging_balances || {};
    const overdue = overdueAmount(dashboardData);
    const total = dashboardData?.total_receivables;
    const rows = monthlySales?.data || [];
    const sold = sum(rows, 'sales_amount');
    const paid = sum(rows, 'payment_amount');
    const terms = averagePaymentPeriod?.company_net_terms;

    // A past "As of" date says so on the tile, which stays in view when the Aging chart is closed
    const asOf = reportDate ? formatDate(reportDate, false, true) : null;

    const receivablesNote = () => {
        if (!total) return undefined;
        if (overdue >= total) return 'All overdue';
        if (overdue <= 0) return 'Nothing overdue';
        return `${formatCurrencySI(overdue, currency)} overdue, ${percent(overdue / total)}`;
    };
    // Faster is good news, in green; slower just says so
    const daysTone = latest?.change < 0 ? 'text-good' : undefined;
    const daysNote = latest?.note && (latest.change ? `${latest.change < 0 ? '↓' : '↑'} ${latest.note}` : latest.note);

    const tiles = [
        !collectionsFailed && {
            key: 'collections',
            title: 'Collections',
            face: collections ? collectionsHeadline(collections) : { label: 'Collected last week', isLoading: true },
            spark: <Sparkline kind="bars" values={recentWeeks} />,
            line: collections && collectionsLine(collections),
            action: VIEW_REPORT,
            // A year of weeks is a barcode at phone width, so a phone gets the last quarter
            body: collections ? <CollectionsChart report={shownWeeks} reportHref={REPORT} figures={false} /> : <ChartWeeksSkeleton />,
        },
        {
            key: 'aging',
            title: 'Aging balance',
            face: {
                label: asOf ? `Receivables as of ${asOf}` : 'Total receivables',
                value: total ? formatCurrencySI(total, currency) : null,
                empty: asOf ? 'No open invoices on this date' : 'Appears once you send an invoice',
                note: receivablesNote(),
                tone: total && overdue > 0 ? 'status-critical' : undefined,
                isLoading: !dashboardData,
            },
            spark: <SegmentSpark segments={AGE_BUCKETS.map((bucket) => ({ key: bucket, value: Number(balances[bucket]?.amount) || 0, color: AGING_BALANCE_GRAPH_COLOR_MAPPING[bucket] }))} />,
            line: agingLine(balances, total, overdue),
            action: <DateInput value={reportDate} onChange={setReportDate} labelPrefix="As of " defaultLabel="today" />,
            body: <AgingBalancesPieChart dashboardData={dashboardData} asOfDate={reportDate} isLoading={!dashboardData} wide />,
        },
        {
            key: 'sales',
            title: 'Monthly sales',
            face: {
                label: rows.length > 1 ? `Sales, last ${rows.length} months` : `Sales${rows[0] ? `, ${rows[0].display_month_string || rows[0].month}` : ''}`,
                value: sold > 0 || paid > 0 ? formatCurrencySI(sold, currency) : null,
                empty: 'Appears after your first invoice',
                note: `${formatCurrencySI(paid, currency)} paid`,
                isLoading: !monthlySales,
            },
            spark: <Sparkline values={rows.map((row) => row.sales_amount || 0)} />,
            line: salesLine(sold, paid, currency),
            body: <SalesBody rows={rows} currency={currency} sold={sold} paid={paid} />,
        },
        {
            key: 'period',
            title: 'Days to pay',
            face: {
                label: 'Days to pay',
                value: latest ? `${latest.days} days` : null,
                empty: 'Appears after your first invoice is paid',
                note: daysNote,
                tone: daysTone,
                isLoading: !averagePaymentPeriod,
            },
            spark: <Sparkline values={points.filter((point) => point.number_of_days != null).map((point) => point.number_of_days)} reference={terms} />,
            line: daysLine(latest, terms, usesMedian(averagePaymentPeriod?.data)),
            body: <PaymentLine points={points} netTerms={terms} height={240} />,
        },
    ].filter(Boolean);
    const open = chosen === undefined ? tiles[0].key : chosen;
    const toggle = (key) => setChosen(open === key ? null : key);
    const current = tiles.find((tile) => tile.key === open);

    return (
        <Box round="xsmall" border elevation="medium" background="background" overflow="hidden">
            <Wrap>
                <Tiles $line={color('border-weak')} style={{ '--tiles': tiles.length }}>
                    {tiles.map((tile, index) => (
                        <TileButton
                            key={tile.key}
                            style={{ '--order': index * 2 }}
                            type="button"
                            aria-expanded={open === tile.key}
                            onClick={() => toggle(tile.key)}
                            $open={open === tile.key}
                            $bg={color('background')}
                            $hoverBg={color('hover-subtle')}
                            $text={color('text')}
                            $accent={color('focus')}
                        >
                            <TileFace {...tile.face} open={open === tile.key} spark={tile.spark} showSpark={!isPhone} />
                        </TileButton>
                    ))}
                    {current && (
                        <Body data-body style={{ '--order': tiles.indexOf(current) * 2 + 1 }} $bg={color('background')}>
                            <Box pad="medium" gap="medium">
                                <Headline title={current.title} line={current.line} action={current.action} />
                                {current.body}
                            </Box>
                        </Body>
                    )}
                </Tiles>
            </Wrap>
        </Box>
    );
};

export default DashboardStrip;
